import React, { useCallback, useEffect, useState, useRef, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
const recipeSlides = [{
  title: 'Panes de Molde y Sandwich',
  image: 'https://cdn.magicpatterns.com/uploads/4bq9LxCEVdwqq8HLpM4S9A/recetas1_(1).webp',
  description: 'Pan de molde, pan suave, brioche, pan para hamburguesas, hot dog y más'
}, {
  title: 'Panes Integrales y de Semillas',
  image: 'https://cdn.magicpatterns.com/uploads/tEbwWmnoGoBHQoguydffAM/recetas2_(1).webp',
  description: 'Pan integral, multigranos, semillas, quinoa, avena, escandinavo, y muchos más'
}, {
  title: 'Panes Rápidos',
  image: 'https://cdn.magicpatterns.com/uploads/nXpKdif7YaPBUxS2v5KzGx/recetas3_(1).webp',
  description: 'Pan de sartén, pan exprés 20 minutos, microondas, desayuno rápido'
}, {
  title: 'Panes Planos e Italianos',
  image: 'https://cdn.magicpatterns.com/uploads/qt8AejoqMD2jetG1fn9qAm/recetas5_(1).webp',
  description: 'Pizza, focaccia, pita, naan, ciabatta, pan árabe, y muchos más'
}, {
  title: 'Panes Especiales y Artesanales',
  image: 'https://cdn.magicpatterns.com/uploads/65Xqtvc46VL4xA63TV2sCw/recetas4_(1).webp',
  description: 'Pan trenzado, festivo, de especias, mediterráneo, francés, alemán y más!'
}, {
  title: 'Pasteles de Chocolate',
  image: 'https://cdn.magicpatterns.com/uploads/qJTVLgcN4nKPmuN7Y9QNzJ/recetas6.webp',
  description: 'Chocolate, cacao intenso, marmolado, chocolate con coco y más'
}, {
  title: 'Bizcochos Cítricos y Frutales',
  image: 'https://cdn.magicpatterns.com/uploads/gqJPq9PhnD1jtZMJ9ZaYGg/recetas7_(1).webp',
  description: 'Limón, naranja, manzana, arándanos, frutos rojos, pera y más'
}, {
  title: 'Bizcochos de Coco y Frutos Secos',
  image: 'https://cdn.magicpatterns.com/uploads/danWHxVe6apEXDApLboUKL/recetas8_(1).webp',
  description: 'Coco húmedo, almendra, coco tostado, frutos secos y mucho más'
}, {
  title: 'Bizcochos Saludables',
  image: 'https://cdn.magicpatterns.com/uploads/hTegrUNekaPRJV1gzekFhU/recetas9_(1)_(1).webp',
  description: 'Zanahoria, avena, yogur, semillas, miel natural, plátano'
}, {
  title: 'Postres Cremosos',
  image: 'https://cdn.magicpatterns.com/uploads/emF7dMGrAxuqyXJrV3tq52/recetas10_(1).webp',
  description: 'Flanes, budines, cremas'
}, {
  title: 'Tartas y Cheesecakes',
  image: 'https://cdn.magicpatterns.com/uploads/5JBEmpco6WbzzvBF2foyVk/recetas11)_(1).webp',
  description: 'Bases sin gluten, rellenos sin azúcar. Un manjar!'
}, {
  title: 'Galletas y Bocados',
  image: 'https://cdn.magicpatterns.com/uploads/rcF341xfVagn9ZK9k1uJBN/recetas12_(1).webp',
  description: 'Cookies, alfajores, barras energéticas y mucho más!'
}, {
  title: 'Postres de Chocolate',
  image: 'https://cdn.magicpatterns.com/uploads/8joBkvrY6xRRjPeGqb5Vxi/recetas13)_(1).webp',
  description: 'Brownies, mousse, trufas y decenas de opciones más.'
}];
const variants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 300 : -300,
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -300 : 300,
    opacity: 0
  })
};
export function RecipesSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const goTo = useCallback((index: number, dir: number) => {
    setDirection(dir);
    setActiveIndex((index % recipeSlides.length + recipeSlides.length) % recipeSlides.length);
  }, []);
  const prev = useCallback(() => goTo(activeIndex - 1, -1), [activeIndex, goTo]);
  const next = useCallback(() => goTo(activeIndex + 1, 1), [activeIndex, goTo]);
  // Auto-advance every 5 seconds
  useEffect(() => {
    if (isPaused) return;
    intervalRef.current = setInterval(() => {
      goTo(activeIndex + 1, 1);
    }, 5000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [activeIndex, isPaused, goTo]);
  const slide = recipeSlides[activeIndex];
  return <section className="py-10 md:py-16 px-4 bg-white" data-id="element-204">
      <div className="max-w-4xl mx-auto" data-id="element-205">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-10" data-id="element-206">
          <h2 className="text-2xl md:text-4xl font-heading font-bold text-brand-brown leading-snug max-w-2xl mx-auto" data-id="element-207">
            Estas son solo algunas de las recetas que te esperan
          </h2>
          <p className="mt-4 text-brand-brownLight text-lg max-w-xl mx-auto leading-relaxed" data-id="element-208">
            Aplica la técnica correcta y logra resultados desde el primer
            intento.
          </p>
        </motion.div>

        {/* Carousel */}
        <div className="relative" onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)} data-id="element-209">
          {/* Arrow left */}
          <button onClick={prev} aria-label="Anterior" className="absolute left-0 md:-left-5 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-white/90 shadow-lg border border-brand-brown/10 text-brand-brown hover:bg-brand-cream cursor-pointer transition-colors" data-id="element-210">
            <ChevronLeftIcon className="w-5 h-5" data-id="element-211" />
          </button>

          {/* Arrow right */}
          <button onClick={next} aria-label="Siguiente" className="absolute right-0 md:-right-5 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-white/90 shadow-lg border border-brand-brown/10 text-brand-brown hover:bg-brand-cream cursor-pointer transition-colors" data-id="element-212">
            <ChevronRightIcon className="w-5 h-5" data-id="element-213" />
          </button>

          {/* Slide area */}
          <div className="overflow-hidden rounded-2xl mx-8 md:mx-0" data-id="element-214">
            <AnimatePresence mode="wait" custom={direction} data-id="element-215">
              <motion.div key={activeIndex} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.35,
              ease: 'easeInOut'
            }} className="bg-brand-cream rounded-2xl overflow-hidden border border-brand-brown/5 shadow-sm" data-id="element-216">
                {/* Title */}
                <div className="px-5 pt-5 pb-3" data-id="element-217">
                  <h3 className="text-xl md:text-2xl font-bold text-brand-brown text-center leading-snug" data-id="element-218">
                    {slide.title}
                  </h3>
                </div>

                {/* Image */}
                <div className="px-4" data-id="element-219">
                  <img src={slide.image} alt={slide.title} className="w-full h-56 md:h-72 object-cover rounded-xl" loading="lazy" decoding="async" width={800} height={288} fetchPriority="low" data-id="element-220" />
                </div>

                {/* Description */}
                <div className="px-5 pt-3 pb-5" data-id="element-221">
                  <p className="text-brand-brownLight text-base leading-relaxed text-center" data-id="element-222">
                    {slide.description}
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Counter + dots */}
        </div>
      </div>
    </section>;
}